import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
const VIEWBOX = "-78.75,44.35,-69.25,37.10";
const DROP_CLASSES = new Set(["boundary"]);
const DROP_PLACE_TYPES = new Set(["county", "region", "quarter", "island", "city_block"]);

function formatNominatimResult(r) {
  const a = r.address || {};
  const parts = [];
  const raw = r.display_name || "";
  const firstSeg = raw.split(",")[0]?.trim();
  if (r.class === "amenity" || r.class === "tourism" || r.class === "aeroway") {
    if (firstSeg) parts.push(firstSeg);
  }
  if (a.road) {
    const road = a.house_number ? `${a.house_number} ${a.road}` : a.road;
    if (!parts.includes(road)) parts.push(road);
  }
  if (a.city === "New York" && a.suburb) {
    parts.push(a.suburb);
  } else if (a.hamlet || a.town || a.village || a.city) {
    parts.push(a.hamlet || a.town || a.village || a.city);
  }
  if (a.state) parts.push(a.state);
  const label = parts.length > 0 ? parts.join(", ") : raw.split(",").slice(0, 3).join(", ");
  const borough = a.city === "New York" ? (a.suburb || "") : "";
  return { label, borough };
}

function filterNominatim(results) {
  const seen = new Set();
  return results.filter((r) => {
    if (DROP_CLASSES.has(r.class)) return false;
    if (r.class === "place" && DROP_PLACE_TYPES.has(r.type)) return false;
    const { label } = formatNominatimResult(r);
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
}

export default function AddressAutocomplete({ value, onChange, placeholder, className, testId }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const merged = [];

      // 1. Landmarks from our DB
      const { data: landmarks } = await api.get(`/locations?q=${encodeURIComponent(q)}`);
      landmarks.slice(0, 6).forEach((loc) => {
        merged.push({
          label: loc.name,
          sublabel: loc.address,
          source: "landmark",
          auto_flags: loc.auto_flags || {},
          address: loc.address,
        });
      });

      // 2. Nominatim fallback (only if we have < 6 landmark results and query >= 3 chars)
      if (merged.length < 6 && q.length >= 3) {
        const bareAddress = /^\d+\s/.test(q) && !/\b(ny|nj|ct|pa|manhattan|brooklyn|queens|bronx|staten)/i.test(q);
        const nominatimQ = bareAddress ? `${q}, New York` : q;

        const params = new URLSearchParams({
          q: nominatimQ, format: "json", countrycodes: "us",
          viewbox: VIEWBOX, bounded: "0", limit: "8",
          addressdetails: "1",
        });
        const res = await fetch(`${NOMINATIM_URL}?${params}`, {
          headers: { "Accept-Language": "en-US" },
        });
        if (res.ok) {
          const raw = await res.json();
          const filtered = filterNominatim(raw);
          const remaining = 6 - merged.length;
          filtered.slice(0, remaining).forEach((r) => {
            const { label, borough } = formatNominatimResult(r);
            const auto_flags = {};
            if (borough.toLowerCase() === "manhattan") {
              auto_flags.is_congestion_zone = true;
            }
            merged.push({ label, sublabel: "", source: "nominatim", auto_flags, address: label });
          });
        }
      }
      setResults(merged);
      setOpen(merged.length > 0);
      setActiveIdx(-1);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 350);
  };

  const selectResult = (r) => {
    const selected = r.source === "landmark" ? r.address : r.label;
    setQuery(selected);
    onChange(selected);
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((p) => Math.min(p + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((p) => Math.max(p - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      selectResult(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Cleanup debounce
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        ref={inputRef}
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
      />
      {loading && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 border border-muted-foreground/40 border-t-primary rounded-full animate-spin" />
      )}
      {open && results.length > 0 && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-lg max-h-[200px] overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {results.map((r, i) => (
            <button
              key={`${r.label}-${r.source}-${i}`}
              type="button"
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-start gap-2 ${
                i === activeIdx ? "bg-primary/15 text-foreground" : "hover:bg-muted/30 text-foreground"
              }`}
              onMouseDown={(e) => { e.preventDefault(); selectResult(r); }}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`${testId}-option-${i}`}
            >
              <span className="flex-1 min-w-0">
                <span className="block truncate font-medium">{r.label}</span>
                {r.sublabel && <span className="block truncate text-muted-foreground text-[10px]">{r.sublabel}</span>}
              </span>
              {r.source === "landmark" && (
                <span className="shrink-0 text-[9px] uppercase tracking-wider text-primary/60 font-medium mt-0.5">
                  {r.auto_flags?.is_airport_trip ? "Airport" : "Saved"}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
